import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import AccessControl from '../../components/AccessControl';
import './PersonaEditor.css';
import config from '../../config';

const PersonaEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;

  // Get default folder path from navigation state (when creating from within a folder)
  const defaultFolderPath = location.state?.defaultFolderPath || [];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt: '',
    access: ['All'],
    folder_path: defaultFolderPath
  });

  const [files, setFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Files selected but not yet uploaded (create mode)
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (isEditMode) {
      fetchPersona();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPersona = async () => {
    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/personas/${id}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Failed to fetch persona');
      }

      const data = await res.json();
      const persona = data.persona;

      setFormData({
        name: persona.name || '',
        description: persona.description || '',
        prompt: persona.prompt || '',
        access: persona.access && persona.access.length > 0 ? persona.access : ['All'],
        folder_path: persona.folder_path || []
      });

      setFiles(persona.files || []);
    } catch (err) {
      console.error('Error fetching persona:', err);
      setError('Failed to load persona');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAccessChange = useCallback((accessList) => {
    setFormData(prev => ({
      ...prev,
      access: accessList
    }));
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // In create mode, store files to upload after persona is created
    if (!isEditMode) {
      const newPendingFiles = selectedFiles.map(file => ({
        file: file,
        name: file.name,
        size: file.size,
        pending: true
      }));
      setPendingFiles(prev => [...prev, ...newPendingFiles]);
      e.target.value = ''; // Reset file input
      return;
    }

    // In edit mode, upload immediately
    setUploading(true);
    setError(null);

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${config.api.baseUrl}/synthetic-audience/personas/${id}/files`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to upload file');
        }

        const data = await res.json();
        setFiles(prev => [...prev, data.file]);
      } catch (err) {
        console.error('Error uploading file:', err);
        alert(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setUploading(false);
    e.target.value = ''; // Reset file input
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/personas/${id}/files/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setFiles(files.filter(f => f.openai_file_id !== fileId));
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete file');
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file');
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a persona name');
      return;
    }

    if (!formData.prompt.trim()) {
      alert('Please enter a persona prompt');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isEditMode
        ? `${config.api.baseUrl}/synthetic-audience/personas/${id}`
        : `${config.api.baseUrl}/synthetic-audience/personas`;

      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save persona');
      }

      const data = await res.json();
      const personaId = data.persona._id;

      // If creating new and there are pending files, upload them
      if (!isEditMode && pendingFiles.length > 0) {
        setUploading(true);
        let uploadErrors = [];

        for (const pendingFile of pendingFiles) {
          try {
            const formData = new FormData();
            formData.append('file', pendingFile.file);

            const uploadRes = await fetch(`${config.api.baseUrl}/synthetic-audience/personas/${personaId}/files`, {
              method: 'POST',
              credentials: 'include',
              body: formData
            });

            if (!uploadRes.ok) {
              const uploadData = await uploadRes.json();
              throw new Error(uploadData.error || 'Failed to upload file');
            }
          } catch (err) {
            console.error(`Error uploading ${pendingFile.name}:`, err);
            uploadErrors.push(`${pendingFile.name}: ${err.message}`);
          }
        }

        setUploading(false);

        if (uploadErrors.length > 0) {
          alert(`Persona created, but some files failed to upload:\n${uploadErrors.join('\n')}`);
        }
      }

      // If creating new, navigate to edit mode
      if (!isEditMode) {
        navigate(`/synthetic-audience-workshop/personas/${personaId}`);
      } else {
        alert('Persona updated successfully');
      }
    } catch (err) {
      console.error('Error saving persona:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Layout>
        <div className="persona-editor-loading">
          <div className="persona-editor-loading-content">
            <div className="persona-editor-loading-spinner"></div>
            <div className="persona-editor-loading-text">Loading persona...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="persona-editor">
      <header className="editor-header">
        <div>
          <h1>{isEditMode ? 'Edit Persona' : 'Create New Persona'}</h1>
          <p className="header-subtitle">
            {isEditMode ? 'Update persona details and training files' : 'Define a synthetic audience persona with custom characteristics'}
          </p>
        </div>
        <button
          className="btn-back"
          onClick={() => navigate('/synthetic-audience-workshop')}
        >
          ← Back to Workshop
        </button>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="editor-form">
        {/* Basic Information */}
        <section className="persona-section">
          <h2>Basic Information</h2>

          <div className="form-group">
            <label htmlFor="name">Persona Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Tech-Savvy Millennial"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of this persona..."
              rows="3"
            />
          </div>
        </section>

        {/* Persona Prompt */}
        <section className="persona-section">
          <h2>Persona Instructions *</h2>
          <p className="section-help">
            Define the persona's characteristics, background, preferences, and behavior patterns. Be specific and detailed.
          </p>

          <div className="form-group">
            <textarea
              id="prompt"
              name="prompt"
              value={formData.prompt}
              onChange={handleInputChange}
              placeholder="You are a 28-year-old software engineer living in San Francisco. You value efficiency and innovation. You're an early adopter of new technologies and prefer digital solutions over traditional methods. You're environmentally conscious and prefer sustainable brands..."
              rows="12"
              required
            />
          </div>
        </section>

        {/* Training Files */}
        <section className="persona-section">
          <h2>Training Files</h2>
          <p className="section-help">
            Upload files (PDF, TXT, DOCX, CSV, JSON) to train this persona with additional context and knowledge.
            {!isEditMode && " Files will be uploaded after the persona is created."}
          </p>

            <div className="file-upload-area">
              <input
                type="file"
                id="file-upload"
                multiple
                accept=".pdf,.txt,.docx,.doc,.md,.csv,.json,.xml,.html,.htm,.c,.cpp,.cs,.java,.py,.js,.ts,.jsx,.tsx,.php,.rb,.go"
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-upload" className="btn-upload">
                {uploading ? 'Uploading...' : '+ Upload Files'}
              </label>
              <span className="upload-hint">
                Supported: PDF, TXT, DOCX, MD, CSV, JSON, code files (Max 50MB per file)
              </span>
            </div>

            {(files.length > 0 || pendingFiles.length > 0) && (
              <div className="files-list">
                {/* Uploaded files */}
                {files.map((file) => (
                  <div key={file.openai_file_id} className="file-item">
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                      <span className="file-status uploaded">✓ Uploaded</span>
                    </div>
                    <button
                      type="button"
                      className="btn-delete-file"
                      onClick={() => handleDeleteFile(file.openai_file_id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {/* Pending files (in create mode) */}
                {pendingFiles.map((file, index) => (
                  <div key={`pending-${index}`} className="file-item pending">
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                      <span className="file-status pending">⏳ Will upload on save</span>
                    </div>
                    <button
                      type="button"
                      className="btn-delete-file"
                      onClick={() => handleRemovePendingFile(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

        {/* Access Control */}
        <section className="persona-section">
          <h2>Access Control</h2>
          <p className="section-help">
            Control who can access and use this persona.
          </p>
          <AccessControl
            value={formData.access || ['All']}
            onChange={handleAccessChange}
          />
        </section>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate('/synthetic-audience-workshop')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Persona')}
          </button>
        </div>
      </form>
    </div>
    </Layout>
  );
};

export default PersonaEditor;
